import { ChiroUpAPI } from '@chiroup/client-core';
import { Input, OpenClosedStates } from '@chiroup/components';
import { Plan } from '@chiroup/core';
import { BuildingLibraryIcon, PencilIcon } from '@heroicons/react/24/outline';
import {
  CheckIcon,
  CreditCardIcon,
  InformationCircleIcon,
  MinusIcon,
} from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Tooltip } from 'react-tooltip';
import { MeContext } from '../../../contexts/me.context';
import { ToastContext, ToastTypes } from '../../../contexts/toast.context';
import bingham from '../../../images/bingham.jpg';
import gregory from '../../../images/gregory.jpg';
import Button, { ButtonColors } from '../../common/Button';
import ConfirmModal from '../../common/ConfirmModal';
import Announcement from '../../layout/Announcement';
import BillingSection from './BillingSection';
import BillingSectionMobile from './BillingSectionMobile';

dayjs.extend(isSameOrAfter);

const cardIcons: any = {
  visa: (
    <svg
      aria-hidden="true"
      width={36}
      height={24}
      viewBox="0 0 36 24"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-auto"
    >
      <rect width={36} height={24} rx={4} fill="#224DBA" />
      <path
        d="M10.925 15.673H8.874l-1.538-6c-.073-.276-.228-.52-.456-.635A6.575 6.575 0 005 8.403v-.231h3.304c.456 0 .798.347.855.75l.798 4.328 2.05-5.078h1.994l-3.076 7.5zm4.216 0h-1.937L14.8 8.172h1.937l-1.595 7.5zm4.101-5.422c.057-.404.399-.635.798-.635a3.54 3.54 0 011.88.346l.342-1.615A4.808 4.808 0 0020.496 8c-1.88 0-3.248 1.039-3.248 2.481 0 1.097.969 1.673 1.653 2.02.74.346 1.025.577.968.923 0 .519-.57.75-1.139.75a4.795 4.795 0 01-1.994-.462l-.342 1.616a5.48 5.48 0 002.108.404c2.108.057 3.418-.981 3.418-2.539 0-1.962-2.678-2.077-2.678-2.942zm9.457 5.422L27.16 8.172h-1.652a.858.858 0 00-.798.577l-2.848 6.924h1.994l.398-1.096h2.45l.228 1.096h1.766zm-2.905-5.482l.57 2.827h-1.596l1.026-2.827z"
        fill="#fff"
      />
    </svg>
  ),
  mastercard: (
    <svg
      enable-background="new 0 0 780 500"
      height="500"
      viewBox="0 0 780 500"
      width="780"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-auto"
    >
      <path
        d="m40 0h700c22.092 0 40 17.909 40 40v420c0 22.092-17.908 40-40 40h-700c-22.091 0-40-17.908-40-40v-420c0-22.091 17.909-40 40-40z"
        fill="#16366f"
      />
      <path
        d="m449.01 250c0 99.143-80.37 179.5-179.51 179.5s-179.5-80.361-179.5-179.5c0-99.133 80.362-179.5 179.5-179.5 99.137 0 179.51 80.37 179.51 179.5"
        fill="#d9222a"
      />
      <path
        d="m510.49 70.496c-46.38 0-88.643 17.596-120.5 46.466-6.49 5.889-12.548 12.237-18.125 18.996h36.266c4.966 6.037 9.536 12.388 13.685 19.013h-63.635c-3.827 6.121-7.28 12.469-10.341 19.008h84.312c2.893 6.185 5.431 12.53 7.6 19.004h-99.512c-2.091 6.235-3.832 12.581-5.217 19.009h109.94c2.689 12.49 4.044 25.231 4.041 38.008 0 19.934-3.254 39.113-9.254 57.02h-99.512c2.164 6.479 4.7 12.825 7.595 19.01h84.317c-3.064 6.54-6.52 12.889-10.347 19.013h-63.625c4.154 6.629 8.73 12.979 13.685 18.996h36.258c-5.57 6.772-11.63 13.126-18.13 19.012 31.86 28.867 74.118 46.454 120.5 46.454 99.138-.001 179.51-80.362 179.51-179.5 0-99.13-80.37-179.5-179.51-179.5"
        fill="#ee9f2d"
      />
      <path d="m666.08 350.06c0-3.201 2.592-5.801 5.796-5.801s5.796 2.6 5.796 5.801c0 3.199-2.592 5.799-5.796 5.799-3.202-.001-5.797-2.598-5.796-5.799zm5.796 4.408c2.435-.001 4.407-1.975 4.408-4.408 0-2.433-1.972-4.404-4.404-4.404h-.004c-2.429-.004-4.4 1.963-4.404 4.392v.013c-.003 2.432 1.967 4.406 4.399 4.408.001-.001.003-.001.005-.001zm-.783-1.86h-1.188v-5.094h2.149c.45 0 .908 0 1.305.254.413.278.646.77.646 1.278 0 .57-.337 1.104-.883 1.312l.937 2.25h-1.315l-.78-2.016h-.87v2.016zm0-2.89h.658c.246 0 .504.02.725-.1.196-.125.296-.359.296-.584 0-.195-.12-.42-.288-.516-.207-.131-.536-.101-.758-.101h-.633zm-443.5-80.063c-2.045-.237-2.945-.301-4.35-.301-11.045 0-16.637 3.789-16.637 11.268 0 4.611 2.73 7.546 6.987 7.546 7.938 0 13.659-7.56 14-18.513zm14.171 32.996h-16.146l.371-7.676c-4.925 6.067-11.496 8.95-20.425 8.95-10.562 0-17.804-8.25-17.804-20.229 0-18.024 12.596-28.54 34.217-28.54 2.208 0 5.041.2 7.941.569.605-2.441.763-3.486.763-4.8 0-4.908-3.396-6.738-12.5-6.738-9.533-.108-17.396 2.271-20.625 3.334.204-1.23 2.7-16.658 2.7-16.658 9.712-2.846 16.117-3.917 23.325-3.917 16.733 0 25.596 7.512 25.58 21.712.032 3.805-.597 8.5-1.58 14.671-1.692 10.731-5.32 33.718-5.817 39.322zm-62.158 0h-19.488l11.163-69.997-24.925 69.997h-13.28l-1.64-69.597-11.734 69.597h-18.242l15.238-91.054h28.02l1.7 50.966 17.092-50.966h31.167zm354.98-32.996c-2.037-.237-2.942-.301-4.342-.301-11.041 0-16.634 3.789-16.634 11.268 0 4.611 2.726 7.546 6.983 7.546 7.939 0 13.664-7.56 13.993-18.513zm14.183 32.996h-16.145l.365-7.676c-4.925 6.067-11.5 8.95-20.42 8.95-10.566 0-17.8-8.25-17.8-20.229 0-18.024 12.587-28.54 34.212-28.54 2.208 0 5.037.2 7.934.569.604-2.441.763-3.486.763-4.8 0-4.908-3.392-6.738-12.496-6.738-9.533-.108-17.388 2.271-20.63 3.334.205-1.23 2.709-16.658 2.709-16.658 9.713-2.846 16.113-3.917 23.312-3.917 16.741 0 25.604 7.512 25.588 21.712.032 3.805-.597 8.5-1.58 14.671-1.682 10.731-5.32 33.718-5.812 39.322zm-220.39-1.125c-5.334 1.68-9.492 2.399-14 2.399-9.963 0-15.4-5.725-15.4-16.267-.142-3.27 1.433-11.879 2.67-19.737 1.125-6.917 8.45-50.53 8.45-50.53h19.371l-2.262 11.209h11.7l-2.643 17.796h-11.742c-2.25 14.083-5.454 31.625-5.491 33.95 0 3.817 2.037 5.483 6.67 5.483 2.221 0 3.941-.226 5.255-.7zm59.391-.6c-6.654 2.033-13.075 3.017-19.879 3-21.683-.021-32.987-11.346-32.987-33.032 0-25.313 14.38-43.947 33.9-43.947 15.97 0 26.17 10.433 26.17 26.796 0 5.429-.7 10.729-2.387 18.212h-38.575c-1.304 10.742 5.57 15.217 16.837 15.217 6.935 0 13.188-1.43 20.142-4.663zm-10.887-43.9c.107-1.543 2.054-13.217-9.013-13.217-6.171 0-10.583 4.704-12.38 13.217zm-123.42-5.017c0 9.367 4.541 15.825 14.841 20.676 7.892 3.709 9.113 4.809 9.113 8.17 0 4.617-3.48 6.7-11.192 6.7-5.812 0-11.22-.907-17.458-2.92 0 0-2.563 16.32-2.68 17.101 4.43.966 8.38 1.861 20.28 2.19 20.562 0 30.058-7.829 30.058-24.75 0-10.175-3.975-16.146-13.737-20.633-8.171-3.75-9.109-4.588-9.109-8.046 0-4.004 3.238-6.046 9.538-6.046 3.825 0 9.05.408 14 1.113l2.775-17.175c-5.046-.8-12.696-1.442-17.15-1.442-21.8 0-29.346 11.387-29.279 25.062m229.09-23.116c5.413 0 10.459 1.42 17.413 4.92l3.187-19.762c-2.854-1.12-12.904-7.7-21.416-7.7-13.042 0-24.066 6.47-31.82 17.15-11.31-3.746-15.959 3.825-21.659 11.367l-5.062 1.179c.383-2.483.73-4.95.613-7.446h-17.896c-2.445 22.917-6.779 46.13-10.171 69.075l-.884 4.976h19.496c3.254-21.143 5.038-34.681 6.121-43.842l7.342-4.084c1.096-4.08 4.529-5.458 11.416-5.292-.926 5.008-1.389 10.09-1.383 15.184 0 24.225 13.071 39.308 34.05 39.308 5.404 0 10.042-.712 17.221-2.657l3.431-20.76c-6.46 3.18-11.761 4.676-16.561 4.676-11.328 0-18.183-8.362-18.183-22.184-.001-20.05 10.195-34.108 24.745-34.108" />
      <path
        d="m185.21 297.24h-19.491l11.17-69.988-24.925 69.988h-13.282l-1.642-69.588-11.733 69.588h-18.243l15.238-91.042h28.02l.788 56.362 18.904-56.362h30.267z"
        fill="#fff"
      />
      <path d="m647.52 211.6-4.319 26.308c-5.33-7.012-11.054-12.087-18.612-12.087-9.834 0-18.784 7.454-24.642 18.425-8.158-1.692-16.597-4.563-16.597-4.563l-.004.067c.658-6.133.92-9.875.862-11.146h-17.9c-2.437 22.917-6.77 46.13-10.157 69.075l-.893 4.976h19.492c2.633-17.097 4.65-31.293 6.133-42.551 6.659-6.017 9.992-11.267 16.721-10.917-2.979 7.206-4.725 15.504-4.725 24.017 0 18.513 9.367 30.725 23.534 30.725 7.141 0 12.62-2.462 17.966-8.17l-.912 6.884h18.433l14.842-91.043zm-24.37 73.942c-6.634 0-9.983-4.909-9.983-14.597 0-14.553 6.271-24.875 15.112-24.875 6.695 0 10.32 5.104 10.32 14.508.001 14.681-6.369 24.964-15.449 24.964z" />
      <path
        d="m233.19 264.26c-2.042-.236-2.946-.3-4.346-.3-11.046 0-16.634 3.788-16.634 11.267 0 4.604 2.73 7.547 6.98 7.547 7.945-.001 13.666-7.559 14-18.514zm14.179 32.984h-16.146l.367-7.663c-4.921 6.054-11.5 8.95-20.421 8.95-10.567 0-17.804-8.25-17.804-20.229 0-18.032 12.591-28.542 34.216-28.542 2.209 0 5.042.2 7.938.571.604-2.442.762-3.487.762-4.808 0-4.908-3.391-6.73-12.496-6.73-9.537-.108-17.395 2.272-20.629 3.322.204-1.226 2.7-16.638 2.7-16.638 9.709-2.858 16.121-3.93 23.321-3.93 16.738 0 25.604 7.518 25.588 21.705.029 3.82-.605 8.512-1.584 14.675-1.687 10.725-5.32 33.725-5.812 39.317zm261.38-88.592-3.192 19.767c-6.95-3.496-12-4.921-17.407-4.921-14.551 0-24.75 14.058-24.75 34.107 0 13.821 6.857 22.181 18.183 22.181 4.8 0 10.096-1.492 16.554-4.677l-3.42 20.75c-7.184 1.959-11.816 2.672-17.226 2.672-20.976 0-34.05-15.084-34.05-39.309 0-32.55 18.059-55.3 43.888-55.3 8.507.001 18.562 3.609 21.42 4.73m31.442 55.608c-2.041-.236-2.941-.3-4.346-.3-11.042 0-16.634 3.788-16.634 11.267 0 4.604 2.729 7.547 6.984 7.547 7.937-.001 13.662-7.559 13.996-18.514zm14.179 32.984h-16.15l.37-7.663c-4.924 6.054-11.5 8.95-20.42 8.95-10.563 0-17.804-8.25-17.804-20.229 0-18.032 12.595-28.542 34.212-28.542 2.213 0 5.042.2 7.941.571.601-2.442.763-3.487.763-4.808 0-4.908-3.392-6.73-12.496-6.73-9.533-.108-17.396 2.272-20.629 3.322.204-1.226 2.704-16.638 2.704-16.638 9.709-2.858 16.116-3.93 23.316-3.93 16.742 0 25.604 7.518 25.583 21.705.034 3.82-.595 8.512-1.579 14.675-1.682 10.725-5.324 33.725-5.811 39.317zm-220.39-1.122c-5.338 1.68-9.496 2.409-14 2.409-9.963 0-15.4-5.726-15.4-16.266-.138-3.281 1.437-11.881 2.675-19.738 1.12-6.926 8.446-50.533 8.446-50.533h19.367l-2.259 11.212h9.942l-2.646 17.788h-9.975c-2.25 14.091-5.463 31.619-5.496 33.949 0 3.83 2.042 5.483 6.671 5.483 2.22 0 3.938-.217 5.254-.692zm59.392-.591c-6.65 2.033-13.08 3.013-19.88 3-21.684-.021-32.987-11.346-32.987-33.033 0-25.321 14.38-43.95 33.9-43.95 15.97 0 26.17 10.429 26.17 26.8 0 5.433-.7 10.733-2.382 18.212h-38.575c-1.306 10.741 5.569 15.221 16.837 15.221 6.93 0 13.188-1.434 20.137-4.676zm-10.892-43.912c.117-1.538 2.059-13.217-9.013-13.217-6.166 0-10.579 4.717-12.375 13.217zm-123.42-5.004c0 9.365 4.542 15.816 14.842 20.675 7.891 3.708 9.112 4.812 9.112 8.17 0 4.617-3.483 6.7-11.187 6.7-5.817 0-11.225-.908-17.467-2.92 0 0-2.554 16.32-2.67 17.1 4.42.967 8.374 1.85 20.274 2.191 20.567 0 30.059-7.829 30.059-24.746 0-10.18-3.971-16.15-13.738-20.637-8.167-3.758-9.112-4.583-9.112-8.046 0-4 3.245-6.058 9.541-6.058 3.821 0 9.046.42 14.004 1.125l2.771-17.18c-5.041-.8-12.691-1.441-17.146-1.441-21.804 0-29.345 11.379-29.283 25.067m398.45 50.629h-18.437l.917-6.893c-5.347 5.717-10.825 8.18-17.967 8.18-14.168 0-23.53-12.213-23.53-30.725 0-24.63 14.521-45.393 31.709-45.393 7.558 0 13.28 3.088 18.604 10.096l4.325-26.308h19.221zm-28.745-17.109c9.075 0 15.45-10.283 15.45-24.953 0-9.405-3.63-14.509-10.325-14.509-8.838 0-15.116 10.317-15.116 24.875-.001 9.686 3.357 14.587 9.991 14.587zm-56.843-56.929c-2.439 22.917-6.773 46.13-10.162 69.063l-.891 4.975h19.491c6.971-45.275 8.658-54.117 19.588-53.009 1.742-9.266 4.982-17.383 7.399-21.479-8.163-1.7-12.721 2.913-18.688 11.675.471-3.787 1.334-7.466 1.163-11.225zm-160.42 0c-2.446 22.917-6.78 46.13-10.167 69.063l-.887 4.975h19.5c6.962-45.275 8.646-54.117 19.569-53.009 1.75-9.266 4.992-17.383 7.4-21.479-8.154-1.7-12.716 2.913-18.678 11.675.47-3.787 1.325-7.466 1.162-11.225zm254.57 68.242c0-3.214 2.596-5.8 5.796-5.8 3.197-.003 5.792 2.587 5.795 5.785v.015c-.001 3.2-2.595 5.794-5.795 5.796-3.2-.002-5.794-2.596-5.796-5.796zm5.796 4.404c2.432.001 4.403-1.97 4.403-4.401v-.002c.003-2.433-1.968-4.406-4.399-4.408h-.004c-2.435.001-4.408 1.974-4.409 4.408.003 2.432 1.976 4.403 4.409 4.403zm-.784-1.87h-1.188v-5.084h2.154c.446 0 .908.008 1.296.254.416.283.654.767.654 1.274 0 .575-.338 1.113-.888 1.317l.941 2.236h-1.319l-.78-2.008h-.87v2.008zm0-2.88h.654c.245 0 .513.018.729-.1.195-.125.295-.361.295-.587-.009-.21-.115-.404-.287-.524-.204-.117-.542-.085-.763-.085h-.629v1.296z"
        fill="#fff"
      />
    </svg>
  ),
  amex: (
    <svg
      enable-background="new 0 0 780 500"
      height="500"
      viewBox="0 0 780 500"
      width="780"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-auto"
    >
      <path
        d="m40 .001h700c22.092 0 40 17.909 40 40v420c0 22.092-17.908 40-40 40h-700c-22.091 0-40-17.908-40-40v-420c0-22.091 17.909-40 40-40z"
        fill="#2557d6"
      />
      <path
        d="m.253 235.69h37.441l8.442-19.51h18.9l8.42 19.51h73.668v-14.915l6.576 14.98h38.243l6.576-15.202v15.138h183.08l-.085-32.026h3.542c2.479.083 3.204.302 3.204 4.226v27.8h94.689v-7.455c7.639 3.92 19.518 7.455 35.148 7.455h39.836l8.525-19.51h18.9l8.337 19.51h76.765v-18.532l11.626 18.532h61.515v-122.51h-60.88v14.468l-8.522-14.468h-62.471v14.468l-7.828-14.468h-84.38c-14.123 0-26.539 1.889-36.569 7.153v-7.153h-58.229v7.153c-6.383-5.426-15.079-7.153-24.75-7.153h-212.74l-14.274 31.641-14.659-31.641h-67.005v14.468l-7.362-14.468h-57.145l-26.539 58.246v64.261h.003zm236.34-17.67h-22.464l-.083-68.794-31.775 68.793h-19.24l-31.858-68.854v68.854h-44.57l-8.42-19.592h-45.627l-8.505 19.592h-23.801l39.241-87.837h32.559l37.269 83.164v-83.164h35.766l28.678 59.587 26.344-59.587h36.485zm-165.9-37.823-14.998-35.017-14.915 35.017zm255.3 37.821h-73.203v-87.837h73.203v18.291h-51.289v15.833h50.06v18.005h-50.061v17.542h51.289zm103.16-64.18c0 14.004-9.755 21.24-15.439 23.412 4.794 1.748 8.891 4.838 10.84 7.397 3.094 4.369 3.628 8.271 3.628 16.116v17.255h-22.104l-.083-11.077c0-5.285.528-12.886-3.458-17.112-3.202-3.09-8.083-3.76-15.973-3.76h-23.523v31.95h-21.914v-87.838h50.401c11.199 0 19.451.283 26.535 4.207 6.933 3.924 11.09 9.652 11.09 19.45zm-27.699 13.042c-3.013 1.752-6.573 1.81-10.841 1.81h-26.62v-19.51h26.982c3.818 0 7.804.164 10.393 1.584 2.842 1.28 4.601 4.003 4.601 7.765 0 3.84-1.674 6.929-4.515 8.351zm62.844 51.138h-22.358v-87.837h22.358zm259.56 0h-31.053l-41.535-65.927v65.927h-44.628l-8.527-19.592h-45.521l-8.271 19.592h-25.648c-10.649 0-24.138-2.257-31.773-9.715-7.701-7.458-11.708-17.56-11.708-33.533 0-13.027 2.395-24.936 11.812-34.347 7.085-7.01 18.18-10.242 33.28-10.242h21.215v18.821h-20.771c-7.997 0-12.514 1.14-16.862 5.203-3.735 3.699-6.298 10.69-6.298 19.897 0 9.41 1.951 16.196 6.023 20.628 3.373 3.476 9.506 4.53 15.272 4.53h9.842l30.884-69.076h32.835l37.102 83.081v-83.08h33.366l38.519 61.174v-61.174h22.445zm-133.2-37.82-15.165-35.017-15.081 35.017zm189.04 178.08c-5.322 7.457-15.694 11.238-29.736 11.238h-42.319v-18.84h42.147c4.181 0 7.106-.527 8.868-2.175 1.665-1.474 2.605-3.554 2.591-5.729 0-2.561-1.064-4.593-2.677-5.811-1.59-1.342-3.904-1.95-7.722-1.95-20.574-.67-46.244.608-46.244-27.194 0-12.742 8.443-26.156 31.439-26.156h43.649v-17.479h-40.557c-12.237 0-21.129 2.81-27.425 7.174v-7.175h-59.985c-9.595 0-20.854 2.279-26.179 7.175v-7.175h-107.12v7.175c-8.524-5.892-22.908-7.175-29.549-7.175h-70.656v7.175c-6.745-6.258-21.742-7.175-30.886-7.175h-79.077l-18.094 18.764-16.949-18.764h-118.13v122.59h115.9l18.646-19.062 17.565 19.062 71.442.061v-28.838h7.021c9.479.14 20.66-.228 30.523-4.312v33.085h58.928v-31.952h2.842c3.628 0 3.985.144 3.985 3.615v28.333h179.01c11.364 0 23.244-2.786 29.824-7.845v7.845h56.78c11.815 0 23.354-1.587 32.134-5.649l.002-22.84zm-354.94-47.155c0 24.406-19.005 29.445-38.159 29.445h-27.343v29.469h-42.591l-26.984-29.086-28.042 29.086h-86.802v-87.859h88.135l26.961 28.799 27.875-28.799h70.021c17.389 0 36.929 4.613 36.929 28.945zm-174.22 40.434h-53.878v-17.48h48.11v-17.926h-48.11v-15.974h54.939l23.969 25.604zm86.81 10.06-33.644-35.789 33.644-34.65zm49.757-39.066h-28.318v-22.374h28.572c7.912 0 13.404 3.09 13.404 10.772 0 7.599-5.238 11.602-13.658 11.602zm148.36-40.373h73.138v18.17h-51.315v15.973h50.062v17.926h-50.062v17.48l51.314.08v18.23h-73.139zm-28.119 47.029c4.878 1.725 8.865 4.816 10.734 7.375 3.095 4.291 3.542 8.294 3.631 16.037v17.418h-22.002v-10.992c0-5.286.531-13.112-3.542-17.198-3.201-3.147-8.083-3.899-16.076-3.899h-23.42v32.09h-22.02v-87.859h50.594c11.093 0 19.173.47 26.366 4.146 6.915 4.004 11.266 9.487 11.266 19.511-.001 14.022-9.764 21.178-15.531 23.371zm-12.385-11.107c-2.932 1.667-6.556 1.811-10.818 1.811h-26.622v-19.732h26.982c3.902 0 7.807.08 10.458 1.587 2.84 1.423 4.538 4.146 4.538 7.903 0 3.758-1.699 6.786-4.538 8.431zm197.82 5.597c4.27 4.229 6.554 9.571 6.554 18.613 0 18.9-12.322 27.723-34.425 27.723h-42.68v-18.84h42.51c4.157 0 7.104-.525 8.95-2.175 1.508-1.358 2.589-3.333 2.589-5.729 0-2.561-1.17-4.592-2.675-5.811-1.675-1.34-3.986-1.949-7.803-1.949-20.493-.67-46.157.609-46.157-27.192 0-12.744 8.355-26.158 31.33-26.158h43.932v18.7h-40.198c-3.984 0-6.575.145-8.779 1.587-2.4 1.422-3.29 3.534-3.29 6.319 0 3.314 2.037 5.57 4.795 6.546 2.311.77 4.795.995 8.526.995l11.797.306c11.895.276 20.061 2.248 25.024 7.065zm86.955-23.52h-39.938c-3.986 0-6.638.144-8.867 1.587-2.312 1.423-3.202 3.534-3.202 6.322 0 3.314 1.951 5.568 4.791 6.544 2.312.771 4.795.996 8.444.996l11.878.304c11.983.284 19.982 2.258 24.86 7.072.891.67 1.422 1.422 2.033 2.175v-25z"
        fill="#fff"
      />
    </svg>
  ),
  discover: (
    <svg
      height="500"
      viewBox="0 0 780 500"
      width="780"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-auto"
    >
      <g fill-rule="evenodd">
        <path
          d="m54.992 0c-30.365 0-54.992 24.63-54.992 55.004v390.992c0 30.38 24.619 55.004 54.992 55.004h670.016c30.365 0 54.992-24.63 54.992-55.004v-390.992c0-30.38-24.619-55.004-54.992-55.004z"
          fill="#4d4d4d"
        />
        <path
          d="m327.152 161.893c8.837 0 16.248 1.784 25.268 6.09v22.751c-8.544-7.863-15.955-11.154-25.756-11.154-19.264 0-34.414 15.015-34.414 34.05 0 20.075 14.681 34.196 35.37 34.196 9.312 0 16.586-3.12 24.8-10.857v22.763c-9.341 4.14-16.911 5.776-25.756 5.776-31.278 0-55.582-22.596-55.582-51.737 0-28.826 24.951-51.878 56.07-51.878zm-97.113.627c11.546 0 22.11 3.72 30.943 10.994l-10.748 13.248c-5.35-5.646-10.41-8.028-16.564-8.028-8.853 0-15.3 4.745-15.3 10.989 0 5.354 3.619 8.188 15.944 12.482 23.365 8.044 30.29 15.176 30.29 30.926 0 19.193-14.976 32.553-36.32 32.553-15.63 0-26.994-5.795-36.458-18.872l13.268-12.03c4.73 8.61 12.622 13.222 22.42 13.222 9.163 0 15.947-5.952 15.947-13.984 0-4.164-2.055-7.734-6.158-10.258-2.066-1.195-6.158-2.977-14.2-5.647-19.291-6.538-25.91-13.527-25.91-27.185 0-16.225 14.214-28.41 32.846-28.41zm234.723 1.728h22.437l28.084 66.592 28.446-66.592h22.267l-45.494 101.686h-11.053zm-397.348.152h30.15c33.312 0 56.534 20.382 56.534 49.641 0 14.59-7.104 28.696-19.118 38.057-10.108 7.901-21.626 11.445-37.574 11.445h-29.992zm96.135 0h20.54v99.143h-20.54zm411.734 0h58.252v16.8h-37.725v22.005h36.336v16.791h-36.336v26.762h37.726v16.785h-58.252v-99.143zm71.858 0h30.455c23.69 0 37.265 10.71 37.265 29.272 0 15.18-8.514 25.14-23.986 28.105l33.148 41.766h-25.26l-28.429-39.828h-2.678v39.828h-20.515zm20.515 15.616v30.025h6.002c13.117 0 20.069-5.362 20.069-15.328 0-9.648-6.954-14.697-19.745-14.697zm-579.716 1.183v65.559h5.512c13.273 0 21.656-2.394 28.11-7.88 7.103-5.955 11.376-15.465 11.376-24.98 0-9.499-4.273-18.725-11.376-24.681-6.785-5.78-14.837-8.018-28.11-8.018z"
          fill="#fff"
        />
        <path
          d="m415.13 161.21c30.941 0 56.022 23.58 56.022 52.709v.033c0 29.13-25.081 52.742-56.021 52.742s-56.022-23.613-56.022-52.742v-.033c0-29.13 25.082-52.71 56.022-52.71zm364.85 127.15c-26.05 18.33-221.08 149.34-558.75 212.62h503.76c30.365 0 54.992-24.63 54.992-55.004v-157.62z"
          fill="#f47216"
        />
      </g>
    </svg>
  ),
  default: <CreditCardIcon className="h-6 w-auto text-primary-500" />,
};

export type Tier = {
  name: string;
  value: 'basic' | 'plus' | 'premium';
  price: { USA: number; CAN: number; annual?: { USA: number; CAN: number } };
  description: string;
};

export const tiers: Tier[] = [
  {
    name: 'Basic',
    value: 'basic',
    price: { USA: 149, CAN: 149 },
    description: 'Every online resource ChiroUp has to offer',
  },
  {
    name: 'Plus',
    value: 'plus',
    price: { USA: 199, CAN: 199 },
    description: 'Basic Features + social media posts',
  },
  {
    name: 'Premium',
    value: 'premium',
    price: { USA: 249, CAN: 249 },
    description: 'Plus Features + community e-newsletters',
  },
];

export const sections = [
  {
    name: 'Online tools',
    features: [
      {
        name: 'Customizable, Condition-Specific Patient Reports with Online & Mobile Access',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: '“Best Practice” Protocols for 100+ Conditions (with Video Tutorials)',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Auto-Generated MD Initial & Release Summaries',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Automatic Google Reviews',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Clinic Dashboard with Key Practice Stats & Outcomes',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Professionally Designed Marketing Campaigns & Samples',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Free Provider & Staff Training',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
    ],
  },
  {
    name: 'Marketing',
    features: [
      {
        name: '20 Professionally Created, Patient-Centered Social Media Posts Every Month',
        tiers: { Basic: false, Plus: true, Premium: true },
      },
      {
        name: 'Automatic Social Posting',
        tiers: { Basic: false, Plus: true, Premium: true },
      },
    ],
  },
  {
    name: 'Clinical skills',
    features: [
      {
        name: 'Monthly Lay Newsletter',
        tiers: { Basic: false, Plus: false, Premium: true },
      },
      {
        name: 'Monthly MD Newsletter',
        tiers: { Basic: false, Plus: false, Premium: true },
      },
      {
        name: 'Quarterly Attorney Newsletter',
        tiers: { Basic: false, Plus: false, Premium: true },
      },
    ],
  },
];

const faqs = [
  {
    id: 1,
    question: 'What happens during my trial?',
    answer: `Getting your account started takes as little as 3 minutes! Once you complete the short sign-up process, you will have free access to ChiroUp’s platform for 14 days. You and your entire office will have the ability to utilize ChiroUp’s resources. No credit card is required. Your trial will include all of the Basic Plan features. For Plus or Premium add-ons, contact our sales team.`,
  },
  {
    id: 2,
    question: 'Can I try different plans during my trial?',
    answer:
      'Yes! If you would like to see samples of the Plus & Premium add-ons, contact our sales team for them to send samples to your inbox.',
  },
  {
    id: 3,
    question: 'Can I see a demo or speak with someone from the sales team?',
    answer: (
      <>
        Yes! ChiroUp is based in the St. Louis, MO metro area, and is in the
        Central Time Zone. Find a time to connect with our team in our calender
        link here:{' '}
        <a
          href="https://calendly.com/becki-4/30min"
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 hover:text-primary-400"
        >
          Schedule a call now
        </a>
        . Don’t see a time that works for you? We want to work with you! Contact
        us for additional availability:{' '}
        <a
          href="mailto:support@chiroup.com"
          className="text-primary-600 hover:text-primary-400"
        >
          support@chiroup.com
        </a>{' '}
        OR{' '}
        <a
          href="tel:8444624476"
          className="text-primary-600 hover:text-primary-400"
        >
          (844) 462-4476
        </a>{' '}
        EXT. 1.
      </>
    ),
  },
  {
    id: 4,
    question: 'What happens after my trial ends?',
    answer:
      'Once your trial ends, if you decide that you want to continue to have access to ChiroUp (which we sure hope you do, 😎) you will select your subscription plan & plug in your payment information. You can do this directly from your ChiroUp account or by contacting your support call specialist.',
  },
  {
    id: 5,
    question: 'How do I cancel my subscription?',
    answer:
      'If you would like to cancel your subscription, please contact Adrienne at support@chiroup.com or (844) 462-4476.',
  },
];

export type ActionTypes = 'subscribe' | 'update';

const Billing = () => {
  const [numberOfLocations, setNumberOfLocations] = useState(1);
  const { me } = useContext(MeContext);
  const { createToast } = useContext(ToastContext);
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const [announcementText, setAnnouncementText] = useState<string>();
  const [confirmChangeModalState, setConfirmChangeModalState] = useState(
    OpenClosedStates.Closed,
  );
  const [requiresAction, setRequiresAction] = useState(false);
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [extendTrial, setExtendTrial] = useState(false);
  const [noMoreTrials, setNoMoreTrials] = useState(false);
  const { state }: any = useLocation();

  useEffect(() => {
    if (me?.selectedClinic?.subscription?.locations) {
      setNumberOfLocations(
        (me?.selectedClinic?.subscription?.locations || 0) + 1,
      );
    }
  }, [me]);

  const callForHelp = useMemo(() => {
    return (
      me?.selectedClinic?.bypassPaymentGateway ||
      me?.selectedClinic?.freeAccount ||
      me?.selectedClinic?.subscription?.callForHelp
    );
  }, [
    me?.selectedClinic?.bypassPaymentGateway,
    me?.selectedClinic?.freeAccount,
    me?.selectedClinic?.subscription?.callForHelp,
  ]);

  useEffect(() => {
    const billDate = me?.selectedClinic?.billDate
      ? dayjs(me?.selectedClinic?.billDate)
      : null;
    const trialEndDate = me?.selectedClinic?.trialEndDate
      ? dayjs(me?.selectedClinic?.trialEndDate)
      : null;
    const plan = me?.selectedClinic?.plan || 'basic';
    const stripeFailureMessage = me?.selectedClinic?.stripeFailureMessage;
    const freeAccount = me?.selectedClinic?.freeAccount;
    const requiresActionStripe =
      me?.selectedClinic?.subscription?.status === 'requires_action' ||
      me?.selectedClinic?.subscription?.status === 'incomplete';
    const billDateFormatted = billDate?.format('MM/DD/YYYY');
    const notActive =
      me?.selectedClinic?.subscription?.status !== 'active' &&
      me?.selectedClinic?.subscription?.status !== 'trialing';
    const pastDue = me?.selectedClinic?.subscription?.status === 'past_due';
    const billDateHasNotPassed = dayjs(me?.selectedClinic?.billDate) > dayjs();
    const expiredText = notActive ? 'expired' : 'is set to expire';
    let billDateText = billDate ? ` on ${billDateFormatted}` : '';
    if (billDateHasNotPassed && notActive) {
      billDateText = '';
    }
    const billDateTextComplete = ` and your plan ${expiredText}${billDateText}`;
    if (stripeFailureMessage) {
      setAnnouncementText(stripeFailureMessage);
    } else if (requiresActionStripe && billDateHasNotPassed) {
      setAnnouncementText(
        `Look for microdeposits in your bank account and follow the instructions sent to the email you entered for billing to verify your payment method.`,
      );
      setRequiresAction(true);
    } else if (plan === 'trial' && !freeAccount) {
      setAnnouncementText(
        `You are currently on a trial plan${billDateTextComplete}, please purchase a plan below to keep your account active.`,
      );
    } else if (plan === 'education') {
      if (dayjs().isBefore(dayjs(billDateText))) {
        setAnnouncementText(
          `You are currently on an education plan${billDateTextComplete}. If you are no longer eligible to receive an education plan, please purchase a plan below to keep your account active.`,
        );
      } else if (
        dayjs().isSameOrAfter(dayjs(billDateText)) &&
        me?.selectedClinic?.referralCode === 'RECENTGRAD'
      ) {
        setAnnouncementText(
          `Your education plan expired on ${billDateText}. You qualify for our special recent grad pricing! Please purchase a plan to keep your account active.`,
        );
      }
    } else if (notActive) {
      setAnnouncementText(
        `Please update your subscription details below to continue using ChiroUp.`,
      );
    } else {
      setAnnouncementText(undefined);
    }
    const twoWeeksAgo = dayjs().subtract(14, 'day');
    if (
      notActive &&
      !pastDue &&
      !requiresActionStripe &&
      (!trialEndDate || (trialEndDate && trialEndDate.isBefore(twoWeeksAgo))) &&
      typeof me?.selectedClinic?.trialCount === 'number'
    ) {
      if (me?.selectedClinic?.plan?.includes('education')) {
        setExtendTrial(false);
      } else if (me?.selectedClinic?.trialCount < 3) {
        setExtendTrial(true);
      } else if (me?.selectedClinic?.trialCount >= 3) {
        setExtendTrial(true);
        setNoMoreTrials(true);
      }
    }
  }, [
    me?.selectedClinic?.stripeFailureMessage,
    me?.selectedClinic?.plan,
    me?.selectedClinic?.billDate,
    me?.selectedClinic?.trialEndDate,
    me?.selectedClinic?.subscription,
    me?.selectedClinic?.trialCount,
    me?.selectedClinic?.freeAccount,
    me?.selectedClinic?.referralCode,
  ]);

  useEffect(() => {
    if (state?.plan) {
      const plan: Plan = state.plan;
      setSelectedPlan(plan);
    }
  }, [state?.plan]);

  const locationsHasChanged = useMemo(() => {
    const additionalLocationsPaidFor =
      me?.selectedClinic?.subscription?.locations || 0;
    return additionalLocationsPaidFor !== numberOfLocations - 1;
  }, [me, numberOfLocations]);

  const selectPlan = async (plan: Plan, type: ActionTypes = 'subscribe') => {
    setSelectedPlan(plan);
    if (type === 'update') {
      setConfirmChangeModalState(OpenClosedStates.Open);
    } else {
      // Make a call to get the stripe checkout page
      const res = await ChiroUpAPI.post(
        'api',
        `/${me.selectedClinic?.ID}/billing/create`,
        {
          body: {
            plan,
            locations: numberOfLocations - 1,
            promoCode: me.selectedClinic?.referralCode,
          },
        },
      );
      const url = res?.url;
      if (url) {
        window.location.href = url;
      }
    }
  };

  const closeConfirmChangeModal = () => {
    setConfirmChangeModalState(OpenClosedStates.Closed);
  };

  const confirmChange = async () => {
    setSavingUpdate(true);
    try {
      await ChiroUpAPI.post('api', `/${me.selectedClinic?.ID}/billing/change`, {
        body: {
          plan: selectedPlan,
          locations: numberOfLocations - 1,
        },
      });
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      console.error({ err: err?.response?.data?.message });
      let msg = '';
      if (err?.response?.data?.message === 'Cannot update annual plans.') {
        msg =
          'Cannot update annual plans. Please contact support for further assistance.';
      } else {
        msg = 'Failed to change plans!';
      }
      setSavingUpdate(false);
      createToast({
        title: 'Error!',
        description: msg,
        type: ToastTypes.Fail,
        duration: 5000,
      });
      setSavingUpdate(false);
    }
  };

  const onClickExtendTrial = async () => {
    if (!me.selectedClinic?.ID) {
      return;
    }
    try {
      await ChiroUpAPI.post('api', `/${me.selectedClinic.ID}/extend-trial`, {});
      window.location.href = '/';
    } catch (err: any) {
      console.error(err);
      createToast({
        title: 'Error!',
        description: err?.response?.data?.message ? (
          err.response.data.message
        ) : (
          <>Failed to extend trial!</>
        ),
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-darkGray-900 dark:text-darkGray-50">
        {me?.selectedClinic?.stripeFailureMessage ? (
          <Announcement
            text={announcementText || ''}
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
            error
          />
        ) : noMoreTrials ? (
          <Announcement
            text="Your free trial eligibility has expired, but we'd still love to have you onboard! Please choose a plan to continue."
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        ) : extendTrial ? (
          <Announcement
            text="Good news! You qualify for an additional 2 week trial."
            rightSide={
              <Button
                color={ButtonColors.plain}
                className="text-accent-600 text-lg"
                text="Extend my trial"
                onClick={onClickExtendTrial}
              />
            }
            color="accent"
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        ) : announcementText ? (
          <Announcement
            text={announcementText}
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        ) : null}
        <div className="max-w-2xl mx-auto pt-16 sm:py-16 px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
          <div className="px-0 sm:px-4 lg:px-0 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
            <div className="max-w-xl">
              <h2 className="text-4xl font-extrabold text-primary-600 sm:text-5xl sm:tracking-tight lg:text-6xl">
                Subscription
              </h2>
              <p className="mt-5 text-xl text-gray-500 dark:text-darkGray-50">
                It’s Time to Take Your Practice to the Next Level. Get started
                in minutes. No contracts…ever.
              </p>
            </div>
            {me?.selectedClinic &&
              !me.selectedClinic.bypassPaymentGateway &&
              !me.selectedClinic.freeAccount &&
              me?.selectedClinic.stripePaymentMethod &&
              me.selectedClinic.plan &&
              me.selectedClinic.plan !== 'trial' && (
                <div className="flex flex-col gap-1">
                  <div
                    className="bg-gray-50 dark:bg-darkGray-800 overflow-hidden sm:rounded-lg p-4 w-full sm:w-72"
                    style={{ minWidth: '18rem' }}
                  >
                    <dt className="font-medium text-gray-900 dark:text-white flex flex-row justify-between items-center gap-4">
                      <span>Payment information</span>
                      <PencilIcon
                        className="w-4 h-4 text-gray-500 hover:text-gray-400 dark:text-darkGray-400 dark:hover:text-darkGray-300 cursor-pointer"
                        onClick={async () => {
                          const urlToGoTo = await ChiroUpAPI.post(
                            'api',
                            `/${me.selectedClinic?.ID}/billing/update-payment-method`,
                            {},
                          );
                          window.location.href = urlToGoTo;
                        }}
                      />
                    </dt>
                    <dd className="mt-2 space-y-2 sm:flex sm:space-y-0 sm:space-x-4 text-gray-900 dark:text-white">
                      {me?.selectedClinic.stripePaymentMethod?.type ===
                        'card' && (
                        <>
                          <div className="flex-none">
                            {cardIcons[
                              me?.selectedClinic.stripePaymentMethod?.brand
                            ]
                              ? cardIcons[
                                  me?.selectedClinic.stripePaymentMethod?.brand
                                ]
                              : cardIcons.default}
                          </div>
                          <div className="flex-auto">
                            <p>
                              Ending with:{' '}
                              {me?.selectedClinic.stripePaymentMethod.last4}
                            </p>
                            <p>
                              Expires:{' '}
                              {me?.selectedClinic.stripePaymentMethod.exp_month}
                              /{me?.selectedClinic.stripePaymentMethod.exp_year}
                            </p>
                          </div>
                        </>
                      )}
                      {me?.selectedClinic.stripePaymentMethod?.type ===
                        'us_bank_account' && (
                        <>
                          <div className="flex-none">
                            <BuildingLibraryIcon className="h-6 w-auto text-gray-400 dark:text-darkGray-300" />
                          </div>
                          <div className="flex-auto">
                            <p>
                              {me?.selectedClinic.stripePaymentMethod
                                ?.bank_name || 'Bank'}
                            </p>
                            <p>
                              Account #: XXXX
                              {me?.selectedClinic.stripePaymentMethod?.last4}
                            </p>
                          </div>
                        </>
                      )}
                    </dd>
                  </div>
                  <div
                    className="text-primary-500 cursor-pointer text-xs text-right"
                    onClick={async () => {
                      const urlToGoTo = await ChiroUpAPI.post(
                        'api',
                        `/${me.selectedClinic?.ID}/billing/update-payment-method`,
                        {},
                      );
                      window.location.href = urlToGoTo;
                    }}
                  >
                    Billing details
                  </div>
                </div>
              )}
          </div>
        </div>

        {callForHelp ? (
          <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:max-w-7xl lg:px-8">
            <p className="mt-5 text-xl text-gray-500">
              Please contact us at 844.462.4476 ext. 2 if you'd like to change
              your plan.
            </p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="flex flex-row gap-2 justify-end items-start align-top mx-4 sm:mx-0">
              <div className="flex flex-col align-center item-center justify-end text-right">
                <span className="text-gray-900 dark:text-white flex flex-row">
                  Number of locations:
                  <InformationCircleIcon
                    className="h-4 w-4"
                    id="locationsTooltip"
                    data-tooltip-content="Each physicial location that exists for your clinic. Each location will be charged separately."
                  />
                  <Tooltip anchorId="locationsTooltip" />
                </span>
                <a
                  href="/settings/clinic/locations"
                  className="text-primary-600 hover:text-primary-500 cursor-pointer text-xs"
                >
                  Manage locations
                </a>
              </div>
              <Input
                type="number"
                name="locations"
                value={numberOfLocations}
                onChange={(val) => val && val > 0 && setNumberOfLocations(val)}
                className="mb-12 w-32"
                min="1"
                downError="Please contact us to remove locations"
                initialValue={
                  (me?.selectedClinic?.subscription?.locations || 1) + 1
                }
              />
            </div>
            <div className="space-y-6 h-full lg:hidden divide-y divide-gray-300">
              {tiers.map((tier) => (
                <BillingSectionMobile
                  key={tier.value}
                  name={tier.name}
                  description={tier.description}
                  standardPrice={
                    tier.price?.[
                      me.selectedClinic?.country === 'CAN' ? 'CAN' : 'USA'
                    ]
                  }
                  additionalLocationsPrice={
                    me?.selectedClinic?.prices?.additionalLocations
                  }
                  clinicCountry={me.selectedClinic?.country}
                  selectPlan={selectPlan}
                  value={tier.value}
                  currentPlan={
                    me?.selectedClinic?.subscription?.plan ||
                    me?.selectedClinic?.plan
                  }
                  subscriptionPlanPrice={
                    me?.selectedClinic?.prices?.[tier.value]
                  }
                  status={me?.selectedClinic?.subscription?.status}
                  disabled={
                    (!!me?.selectedClinic?.stripeFailureMessage &&
                      !!me?.selectedClinic?.stripePaymentMethod) ||
                    requiresAction
                  }
                  coupon={me?.selectedClinic?.prices?.coupon}
                  additionalLocations={numberOfLocations - 1}
                  locationsHasChanged={locationsHasChanged}
                  appliesToEverything={
                    me?.selectedClinic?.prices?.coupon?.appliesToEverything
                  }
                />
              ))}
            </div>
            <div className="hidden lg:block">
              <table className="w-full h-px table-fixed">
                <thead>
                  <tr>
                    <th
                      className="pb-4 pl-6 pr-6 text-sm font-medium text-gray-900 dark:text-darkGray-50 text-left"
                      scope="col"
                    >
                      <span>Plans</span>
                    </th>
                    {tiers.map((tier) => (
                      <th
                        key={tier.name}
                        className="w-1/4 pb-4 px-6 text-lg leading-6 font-medium text-gray-900 dark:text-darkGray-50 text-left"
                        scope="col"
                      >
                        {tier.name}{' '}
                        {me?.selectedClinic?.subscription?.status ===
                          'active' &&
                          me?.selectedClinic?.plan === tier.value && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-accent-100 text-accent-500">
                              Current plan
                            </span>
                          )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="border-t border-gray-300 dark:border-darkGray-800 divide-y divide-gray-300 dark:divide-darkGray-800">
                  <tr>
                    <th
                      className="py-8 pl-6 pr-6 align-top text-sm font-medium text-gray-900 dark:text-darkGray-50 text-left"
                      scope="row"
                    >
                      <span className="flex flex-col">
                        <span>Pricing</span>
                        <span className="text-gray-500 italic text-xs font-light">
                          Sales Tax may apply
                        </span>
                      </span>
                    </th>
                    {tiers.map((tier) => (
                      <BillingSection
                        key={tier.value}
                        name={tier.name}
                        description={tier.description}
                        standardPrice={
                          tier.price?.[
                            me.selectedClinic?.country === 'CAN' ? 'CAN' : 'USA'
                          ]
                        }
                        additionalLocationsPrice={
                          me?.selectedClinic?.prices?.additionalLocations
                        }
                        clinicCountry={me.selectedClinic?.country}
                        selectPlan={selectPlan}
                        value={tier.value}
                        currentPlan={
                          me?.selectedClinic?.subscription?.plan ||
                          me?.selectedClinic?.plan
                        }
                        subscriptionPlanPrice={
                          me?.selectedClinic?.prices?.[tier.value]
                        }
                        status={me?.selectedClinic?.subscription?.status}
                        disabled={
                          (!!me?.selectedClinic?.stripeFailureMessage &&
                            !!me?.selectedClinic?.stripePaymentMethod) ||
                          requiresAction
                        }
                        coupon={me?.selectedClinic?.prices?.coupon}
                        additionalLocations={numberOfLocations - 1}
                        locationsHasChanged={locationsHasChanged}
                        appliesToEverything={
                          me?.selectedClinic?.prices?.coupon
                            ?.appliesToEverything
                        }
                      />
                    ))}
                  </tr>

                  {sections.map((section) => (
                    <Fragment key={section.name}>
                      <tr>
                        <th
                          className="py-3 pl-6 bg-gray-50 dark:bg-darkGray-900 text-sm font-medium text-gray-900 dark:text-darkGray-50 text-left"
                          colSpan={4}
                          scope="colgroup"
                        >
                          {section.name}
                        </th>
                      </tr>
                      {section.features.map((feature: any) => (
                        <tr key={feature.name}>
                          <th
                            className="py-5 pl-6 pr-6 text-sm font-normal text-gray-500 text-left flex flex-row"
                            scope="row"
                            colSpan={1}
                          >
                            {feature.name}
                          </th>
                          {tiers.map((tier) => (
                            <td
                              key={tier.name}
                              className="py-5 px-6"
                              colSpan={1}
                            >
                              {typeof feature.tiers[tier.name] === 'string' ? (
                                <span className="block text-sm text-gray-700">
                                  {feature.tiers[tier.name]}
                                </span>
                              ) : feature.tiers[tier.name] === true ? (
                                <CheckIcon
                                  className="h-5 w-5 text-green-500 mx-auto"
                                  aria-hidden="true"
                                />
                              ) : (
                                <MinusIcon
                                  className="h-5 w-5 text-gray-400 mx-auto"
                                  aria-hidden="true"
                                />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <section className="bg-primary-600 mt-12 ">
          <div className="max-w-7xl mx-auto md:grid md:grid-cols-2 md:px-6 lg:px-8">
            <div className="py-12 px-4 sm:px-6 md:flex md:flex-col md:py-16 md:pl-0 md:pr-10 md:border-r md:border-primary-400 lg:pr-16">
              <blockquote className="md:flex-grow md:flex md:flex-col">
                <div className="relative text-lg font-medium text-white md:flex-grow">
                  <svg
                    className="absolute top-0 left-0 transform -translate-x-3 -translate-y-2 h-8 w-8 text-primary-400"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p className="relative">
                    “I was on the fence about joining ChiroUp but in the past 2
                    months since joining it has become such an integral part of
                    our practice that I’m not sure what we did before! It makes
                    creating exercise programs and sending letters to patients’
                    MD’s so quick and easy and the patients love it! I would
                    highly recommend ChiroUp as a tool for any evidence-based
                    office!”
                  </p>
                </div>
                <footer className="mt-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 inline-flex rounded-full border-2 border-white">
                      <img
                        className="h-12 w-12 rounded-full object-cover"
                        src={gregory}
                        alt="Dr. Gregory"
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-base font-medium text-white">
                        Dr. Joshua Gregory
                      </div>
                      <div className="text-base font-medium text-primary-200">
                        Hometown Health
                      </div>
                    </div>
                  </div>
                </footer>
              </blockquote>
            </div>
            <div className="py-12 px-4 border-t-2 border-primary-400 sm:px-6 md:py-16 md:pr-0 md:pl-10 md:border-t-0 md:border-l lg:pl-16">
              <blockquote className="md:flex-grow md:flex md:flex-col">
                <div className="relative text-lg font-medium text-white md:flex-grow">
                  <svg
                    className="absolute top-0 left-0 transform -translate-x-3 -translate-y-2 h-8 w-8 text-primary-400"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p className="relative">
                    “I truly don’t know how I practiced without ChiroUp, which
                    might sound like hyperbole but it is sincere. I started with
                    ChiroUp about a year ago with their first version which was
                    good, but their 2.0 blew me away. Hands down this is the
                    best money you can spend on your practice. Their customer
                    service is over the top and very responsive.”
                  </p>
                </div>
                <footer className="mt-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 inline-flex rounded-full border-2 border-white">
                      <img
                        className="h-12 w-12 rounded-full object-cover"
                        src={bingham}
                        alt="Dr. Bingham"
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-base font-medium text-white">
                        Dr. Bryan Bingham
                      </div>
                      <div className="text-base font-medium text-primary-200">
                        Compass Chiropractic
                      </div>
                    </div>
                  </div>
                </footer>
              </blockquote>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="space-y-4">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-darkGray-50">
                Frequently asked questions
              </h2>
              <p className="text-lg text-gray-500">
                Can’t find the answer you’re looking for? Reach out to our{' '}
                <a
                  href="mailto:support@chiroup.com"
                  className="font-medium text-primary-600 hover:text-primary-400"
                >
                  customer support
                </a>{' '}
                team.
              </p>
            </div>
            <div className="mt-12 lg:mt-0 lg:col-span-2">
              <dl className="space-y-12">
                {faqs.map((faq) => (
                  <div key={faq.id}>
                    <dt className="text-lg leading-6 font-medium text-gray-900 dark:text-darkGray-50">
                      {faq.question}
                    </dt>
                    <dd className="mt-2 text-base text-gray-500">
                      {faq.answer}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        state={confirmChangeModalState}
        confirm={confirmChange}
        close={closeConfirmChangeModal}
        confirmText="Change plan"
        description={
          locationsHasChanged &&
          selectedPlan === me?.selectedClinic?.subscription?.plan
            ? `Are you sure you would like to change the number of locations for your account? You will be billed the new amount on your next billing cycle.`
            : `Are you sure you would like to change your plan to ${selectedPlan}? You will be billed the new amount on your next billing cycle.`
        }
        icon={
          <CreditCardIcon
            className="h-6 w-6 text-primary-500"
            aria-hidden="true"
          />
        }
        loading={savingUpdate}
      />
    </>
  );
};

export default Billing;
